import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { enumToTitle } from 'src/utils/text.utils';
import {
  OrderWithCustomer,
  PaginatedOrderResponse,
} from 'src/models/Management.interface';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { getApiErrorMessage } from 'src/utils/errorHandler';

export const useGetOrder = () => {
  const [orders, setOrders] = useState<OrderWithCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();

  const fetchOrders = async (
    page = 1,
    limit = 100,
    search = '',
    sortBy = 'orderNumber',
    order = 'desc',
    status?: string,
    customerId?: string,
    createdStartDate?: Date | null,
    createdEndDate?: Date | null,
    scheduledStartDate?: Date | null,
    scheduledEndDate?: Date | null,
    actualStartDate?: Date | null,
    actualEndDate?: Date | null
  ) => {
    setLoading(true);
    try {
      const params: any = {
        offset: page,
        limit: limit,
        search: search,
        sortBy: sortBy,
        order: order,
      };

      if (status) {
        params.status = enumToTitle(status);
      }
      if (customerId) {
        params.customerId = customerId;
      }
      if (createdStartDate) {
        const date =
          createdStartDate instanceof Date
            ? createdStartDate
            : new Date(createdStartDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        params.createdStartDate = `${year}-${month}-${day}`;
      }
      if (createdEndDate) {
        const date =
          createdEndDate instanceof Date
            ? createdEndDate
            : new Date(createdEndDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        params.createdEndDate = `${year}-${month}-${day}`;
      }
      if (scheduledStartDate) {
        const date =
          scheduledStartDate instanceof Date
            ? scheduledStartDate
            : new Date(scheduledStartDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        params.scheduledStartDate = `${year}-${month}-${day}`;
      }
      if (scheduledEndDate) {
        const date =
          scheduledEndDate instanceof Date
            ? scheduledEndDate
            : new Date(scheduledEndDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        params.scheduledEndDate = `${year}-${month}-${day}`;
      }
      if (actualStartDate) {
        const date =
          actualStartDate instanceof Date
            ? actualStartDate
            : new Date(actualStartDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        params.actualStartDate = `${year}-${month}-${day}`;
      }
      if (actualEndDate) {
        const date =
          actualEndDate instanceof Date
            ? actualEndDate
            : new Date(actualEndDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        params.actualEndDate = `${year}-${month}-${day}`;
      }

      const response = await axiosTokenInstance.get<PaginatedOrderResponse>(
        apiUrls.ORDER_MANAGEMENT,
        {
          params,
          withCredentials: true,
        }
      );

      setOrders(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error: unknown) {
      if ((error as any).response?.status === 404) {
        console.error((error as any).response.data.message);
        setOrders([]);
      } else {
        const errorMessage = await getApiErrorMessage(error);
        showSnackbar(errorMessage, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const deleteOrder = async (orderId: string) => {
    try {
      const response = await axiosTokenInstance.delete(
        `${apiUrls.DELETE_ORDER}/${orderId}`
      );
      showSnackbar(response.data.message, 'success');
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  return {
    orders,
    loading,
    totalItems,
    totalPages,
    currentPage,
    fetchOrders,
    deleteOrder,
  };
};
